import type * as mapboxgl from "mapbox-gl";

interface LabelBackgroundConfig {
    color: string;
}

interface StretchableImageOptions {
    width?: number;
    height?: number;
    borderRadius?: number;
}

const DEFAULT_OPTIONS: Required<StretchableImageOptions> = {
    width: 80,
    height: 24,
    borderRadius: 6,
};

/**
 * Creates an SVG string for a rounded rectangle that can be used as a stretchable image.
 */
function createRoundedRectSVG(width: number, height: number, borderRadius: number, color: string): string {
    return `
        <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
            <rect width="${width}" height="${height}" rx="${borderRadius}" fill="${color}"/>
        </svg>
    `;
}

/**
 * Loads a stretchable image into the map. The image can be stretched horizontally and vertically
 * while maintaining corner radius, making it ideal for text backgrounds that adapt to content size.
 */
function loadStretchableImage(
    map: mapboxgl.Map,
    id: string,
    svg: string,
    options: Required<StretchableImageOptions>,
): void {
    if (map.hasImage(id)) return;

    const { width, height, borderRadius } = options;
    const img = new Image(width, height);

    img.onload = () => {
        // Calculate stretch regions and content area based on border radius
        const margin = borderRadius + 2; // Add small buffer for smooth stretching
        map.addImage(id, img, {
            stretchX: [[margin, width - margin]], // Stretch middle, preserve corners
            stretchY: [[margin, height - margin]], // Stretch middle, preserve corners
            content: [margin, 1, width - margin, height], // Define text content area
        });
    };

    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * Loads label background images into the map for use with symbol layers.
 * Creates stretchable rounded rectangles that can adapt to different text lengths.
 */
export function loadLabelBackgrounds(
    map: mapboxgl.Map,
    configs: Record<string, LabelBackgroundConfig>,
    options?: StretchableImageOptions,
): void {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    Object.entries(configs).forEach(([key, config]) => {
        const svg = createRoundedRectSVG(opts.width, opts.height, opts.borderRadius, config.color);
        const imageId = `label-bg-${key}`;
        loadStretchableImage(map, imageId, svg, opts);
    });
}
